import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { FiChevronsUp, FiChevronsDown } from "react-icons/fi";
import apiClient from "../apiClient";
import "./MITTable.css";

function MITTable() {
  const [mitData, setMitData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [sortColumn, setSortColumn] = useState("employee_name");
  const [sortOrder, setSortOrder] = useState("asc");
  const navigate = useNavigate();

  useEffect(() => {
    const fetchMITData = async () => {
      try {
        setLoading(true);
        // Add maxWeeks=10 parameter and exclude Caleb Cafua
        const excludeName = encodeURIComponent("Caleb Cafua");
        const response = await apiClient.get(`/api/mit?status=ACTIVE&maxWeeks=10&excludeName=${excludeName}`);

        const allEmployees = response.data.flatMap(location =>
          location.employees.map(employee => ({
            ...employee,
            location_name: location.corp,
            current_staffing_row: location.current_staffing_row
          }))
        );

        setMitData(allEmployees);
        setLoading(false);
      } catch (err) {
        console.error("Error fetching MIT data:", err);
        setError("Failed to load MIT data. Please try again.");
        setLoading(false);
      }
    };

    fetchMITData();
  }, []);

  const navigateToProfile = (userId) => {
    navigate(`/employee/${userId}`);
  };

  const handleSort = (column) => {
    const newOrder = sortColumn === column && sortOrder === "asc" ? "desc" : "asc";
    setSortColumn(column);
    setSortOrder(newOrder);
  };

  const getSortedData = () => {
    if (!mitData) return [];

    const sortedData = [...mitData];
    
    sortedData.sort((a, b) => {
      let valueA, valueB;
      
      // Handle special cases for different columns
      switch(sortColumn) {
        case "employee_name":
          valueA = a.employee_name || "";
          valueB = b.employee_name || "";
          break;
        case "location_name":
          valueA = a.location_name || "";
          valueB = b.location_name || "";
          break;
        case "hire_date":
          valueA = new Date(a.hire_date || 0);
          valueB = new Date(b.hire_date || 0);
          break;
        case "tenure":
          // Sort by years first, then months
          valueA = (a.tenure.years * 12) + a.tenure.months;
          valueB = (b.tenure.years * 12) + b.tenure.months;
          break;
        case "weeks_in_position":
          valueA = a.tenure.weeks_in_position || 0;
          valueB = b.tenure.weeks_in_position || 0;
          break;
        default:
          valueA = a[sortColumn] || "";
          valueB = b[sortColumn] || "";
      }
      
      // Handle string comparison
      if (typeof valueA === "string" && typeof valueB === "string") {
        return sortOrder === "asc"
          ? valueA.localeCompare(valueB)
          : valueB.localeCompare(valueA);
      }
      
      // Handle numeric or date comparison
      return sortOrder === "asc" ? valueA - valueB : valueB - valueA;
    });
    
    return sortedData;
  };

  if (loading) return <div className="mit-loading">Loading...</div>;
  if (error) return <div className="mit-error">{error}</div>;

  const sortedData = getSortedData();

  return (
    <div className="mit-table-wrapper">
      <div className="mit-summary">
        Total: {mitData.length} active MIT{mitData.length !== 1 ? "s" : ""}
      </div>
      <div className="mit-table-container">
        <table className="mit-table">
          <thead>
            <tr>
              <th onClick={() => handleSort("employee_name")}>
                NAME
                <span className="sort-icons">
                  <FiChevronsUp
                    className={`sort-icon ${
                      sortColumn === "employee_name" && sortOrder === "asc" ? "active" : ""
                    }`}
                  />
                  <FiChevronsDown
                    className={`sort-icon ${
                      sortColumn === "employee_name" && sortOrder === "desc" ? "active" : ""
                    }`}
                  />
                </span>
              </th>
              <th onClick={() => handleSort("location_name")}>
                LOCATION
                <span className="sort-icons">
                  <FiChevronsUp
                    className={`sort-icon ${
                      sortColumn === "location_name" && sortOrder === "asc" ? "active" : ""
                    }`}
                  />
                  <FiChevronsDown
                    className={`sort-icon ${
                      sortColumn === "location_name" && sortOrder === "desc" ? "active" : ""
                    }`}
                  />
                </span>
              </th>
              <th onClick={() => handleSort("hire_date")}>
                HIRE DATE
                <span className="sort-icons">
                  <FiChevronsUp
                    className={`sort-icon ${
                      sortColumn === "hire_date" && sortOrder === "asc" ? "active" : ""
                    }`}
                  />
                  <FiChevronsDown
                    className={`sort-icon ${
                      sortColumn === "hire_date" && sortOrder === "desc" ? "active" : ""
                    }`}
                  />
                </span>
              </th>
              <th onClick={() => handleSort("tenure")}>
                TENURE
                <span className="sort-icons">
                  <FiChevronsUp
                    className={`sort-icon ${
                      sortColumn === "tenure" && sortOrder === "asc" ? "active" : ""
                    }`}
                  />
                  <FiChevronsDown
                    className={`sort-icon ${
                      sortColumn === "tenure" && sortOrder === "desc" ? "active" : ""
                    }`}
                  />
                </span>
              </th>
              <th onClick={() => handleSort("weeks_in_position")}>
                WEEKS IN POSITION
                <span className="sort-icons">
                  <FiChevronsUp
                    className={`sort-icon ${
                      sortColumn === "weeks_in_position" && sortOrder === "asc" ? "active" : ""
                    }`}
                  />
                  <FiChevronsDown
                    className={`sort-icon ${
                      sortColumn === "weeks_in_position" && sortOrder === "desc" ? "active" : ""
                    }`}
                  />
                </span>
              </th>
            </tr>
          </thead>
          <tbody>
            {sortedData.length > 0 ? (
              sortedData.map((employee) => (
                <tr
                  key={employee.user_id}
                  onClick={() => navigateToProfile(employee.user_id)}
                  className="clickable-row"
                >
                  <td>{employee.employee_name}</td>
                  <td>{employee.location_name}</td>
                  <td>{new Date(employee.hire_date).toLocaleDateString()}</td>
                  <td>
                    {employee.tenure.years > 0
                      ? `${employee.tenure.years}y ${employee.tenure.months}m`
                      : `${employee.tenure.months}m`}
                  </td>
                  <td>{employee.tenure.weeks_in_position} weeks</td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan="5" className="no-data">No MIT data available</td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export default MITTable;